import { useState } from 'react';
import { notification, RcFile } from '@pankod/refine-antd';
import { downloadCSVFile, readCSV } from 'utilities/file';
import { getVariantsDropdown, IVariant } from 'services/variant';
import { findDuplicate } from 'utilities/shared';

export enum Column {
  Sku = 'Item SKU',
  Name = 'Product Name',
  Quantity = 'Quantity',
  UnitPrice = 'Unit Price',
  DiscountCost = 'Discount Cost',
}

export interface CsvData {
  [Column.Sku]: string;
  [Column.Name]: string;
  [Column.Quantity]: number;
  [Column.UnitPrice]: number;
}

export const usePurchaseSaleCsvImport = () => {
  const [isImporting, setIsImporting] = useState<boolean>(false);

  const getCsvTemplate = () => {
    const rows: string[][] = [[Column.Sku, Column.Name, Column.Quantity, Column.UnitPrice]];
    const result = [
      {
        [Column.Sku]: 'P0001',
        [Column.Name]: 'Product 1',
        [Column.Quantity]: '100',
        [Column.UnitPrice]: '150',
      },
      {
        [Column.Sku]: 'P0002',
        [Column.Name]: 'Product 2',
        [Column.Quantity]: '150',
        [Column.UnitPrice]: '200',
      },
      {
        [Column.Sku]: 'P0003',
        [Column.Name]: 'Product 3',
        [Column.Quantity]: '200',
        [Column.UnitPrice]: '250',
      },
    ];
    result.forEach((elem) => {
      rows.push([
        elem[Column.Sku],
        elem[Column.Name],
        elem[Column.Quantity],
        elem[Column.UnitPrice],
      ]);
    });

    downloadCSVFile(rows);
  };

  const onImportCsv = async (file: RcFile) => {
    try {
      setIsImporting(true);
      let rowNumber = 0;
      const data: CsvData[] = await readCSV({
        file,
        transformFunction: (row: Record<string, any>) => {
          rowNumber += 1;
          if (rowNumber === 1) {
            // throw error on missing required columns
            const columns = Object.keys(row).map((key) => key);
            [Column.Sku, Column.Name, Column.Quantity, Column.UnitPrice].forEach(
              (requiredColumn, idx) => {
                const column = columns[idx];
                if (requiredColumn !== column) {
                  throw new Error(
                    `Expected '${requiredColumn}' column at column ${idx + 1} but got '${column}'`
                  );
                }
              }
            );
          }

          const data = {
            [Column.Sku]: row[Column.Sku].trim() as string,
            [Column.Name]: row[Column.Name].trim() as string,
            [Column.Quantity]: +row[Column.Quantity].trim(),
            [Column.UnitPrice]: +row[Column.UnitPrice].trim(),
          };

          // required fields value validation
          if (!data[Column.Sku]) {
            throw new Error(
              `'${Column.Sku}' column is a required field. Which is missing at row ${rowNumber + 1}`
            );
          }
          if (!data[Column.Name]) {
            throw new Error(
              `'${Column.Name}' column is a required field. Which is missing at row ${
                rowNumber + 1
              }`
            );
          }

          // number type validation on column 'Quantity' field
          if (isNaN(data[Column.Quantity])) {
            throw new Error(
              `Please provide valid data for column ${Column.Quantity} at row ${rowNumber + 1}`
            );
          }

          // number type validation on column 'Unit Price' field
          if (isNaN(data[Column.UnitPrice])) {
            throw new Error(
              `Please provide valid data for column ${Column.UnitPrice} at row ${rowNumber + 1}`
            );
          }

          return data;
        },
      });

      // If multiple same variant is found then add its quantity
      const mergedData = data.reduce((previousValue, currentValue) => {
        const {
          [Column.Name]: variantName,
          [Column.Sku]: variantSku,
        } = currentValue;
        const existingVariant = previousValue.find(
          (item) => item[Column.Sku] === variantSku && item[Column.Name] === variantName
        );
        if(existingVariant) throw new Error(`Duplicate variant found for sku: ${variantSku} and name: ${variantName}`)
        previousValue.push(currentValue);
        return previousValue;
      }, [] as CsvData[]);

      // throw error on duplicate sku
      const duplicateSku = findDuplicate(mergedData, Column.Sku);
      const uniqueDuplicates = Array.from(new Set(duplicateSku));
      if (duplicateSku.length) {
        notification.error({
          message: 'Failed',
          description: `Found ${
            duplicateSku.length
          } duplicate values for 'sku' - ${uniqueDuplicates.join(', ')}`,
        });
        return [];
      }

      // Get variants info & warn if no variant info is found
      const skus = mergedData.map((item) => item[Column.Sku]);
      const variants = await getVariantsDropdown({ sku: skus });
      const variantSkus = variants.map((item) => item.sku);
      const notExists = skus.filter((sku) => !variantSkus.includes(sku));
      if (notExists.length > 0) {
        notification.warn({
          message: 'Warning',
          description: `Product "${notExists.join(', ')}" not exists.`,
        });
      }
      const variantInfo: (IVariant & { unitPrice: number; quantity: number })[] = [];
      for (const variant of variants) {
        const data = mergedData.find((item) => item[Column.Sku] === variant.sku);
        if (!data) continue;
        variantInfo.push({
          ...variant,
          unitPrice: data[Column.UnitPrice],
          quantity: data[Column.Quantity],
        });
      }

      return variantInfo;
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err.message,
      });
      return [];
    } finally {
      setIsImporting(false);
    }
  };

  return {
    isImporting,
    getCsvTemplate,
    onImportCsv,
  };
};
